import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { Logo } from "./Logo";
import { AdminLoginModal } from "./AdminLoginModal";
import { CONTACT, CRISIS, SITE } from "../content/site";

const FooterLink = ({ to, children, testId }) => (
  <li>
    <Link
      to={to}
      className="text-sm text-cream/80 underline-offset-4 transition-colors hover:text-gold-soft hover:underline"
      data-testid={testId}
    >
      {children}
    </Link>
  </li>
);

export const Footer = () => {
  const [loginOpen, setLoginOpen] = useState(false);

  return (
    <footer className="bg-sage text-cream" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="flex items-center">
              <Logo className="h-16 w-auto rounded-lg bg-white/95 p-1.5" />
            </div>
            <p className="mt-4 max-w-xs font-serif text-lg italic text-cream/85">
              "{SITE.motto}"
            </p>
          </div>

          <nav aria-label="Footer — foundation" className="md:col-span-2">
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-soft">Foundation</h2>
            <ul className="mt-4 space-y-2.5">
              <FooterLink to="/about" testId="footer-link-about">About TJ</FooterLink>
              <FooterLink to="/news" testId="footer-link-news">News &amp; Updates</FooterLink>
              <FooterLink to="/photos" testId="footer-link-photos">Photos</FooterLink>
              <FooterLink to="/contact" testId="footer-link-contact">Contact</FooterLink>
            </ul>
          </nav>

          <nav aria-label="Footer — take part" className="md:col-span-2">
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-soft">Take part</h2>
            <ul className="mt-4 space-y-2.5">
              <FooterLink to="/walking-for-hope" testId="footer-link-walk">Walking for Hope</FooterLink>
              <FooterLink to="/get-involved" testId="footer-link-involved">Get Involved</FooterLink>
              <FooterLink to="/get-involved#donate" testId="footer-link-donate">Donate</FooterLink>
              <FooterLink to="/support" testId="footer-link-support">Get Support</FooterLink>
            </ul>
          </nav>

          <div className="md:col-span-4">
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-soft">Crisis support</h2>
            <ul className="mt-4 space-y-2.5 text-sm text-cream/80">
              <li>
                <a href={CRISIS.callHref} className="inline-flex items-center gap-2 font-semibold text-cream hover:text-gold-soft" data-testid="footer-call-988-link">
                  <Phone className="h-4 w-4" aria-hidden="true" /> Call or text 988 — 24/7, free
                </a>
              </li>
              <li>
                <a href={CRISIS.chatHref} target="_blank" rel="noopener noreferrer" className="hover:text-gold-soft hover:underline underline-offset-4" data-testid="footer-chat-988-link">
                  988 Lifeline online chat
                </a>
              </li>
              <li className="text-cream/70">Immediate danger? Call 911.</li>
            </ul>
            <h2 className="mt-8 text-xs font-semibold uppercase tracking-[0.2em] text-gold-soft">Follow</h2>
            {CONTACT.socials.length === 0 ? (
              <p className="mt-3 text-sm text-cream/60" data-testid="footer-social-placeholder">
                Social media links will appear here once the foundation's accounts are shared.
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {CONTACT.socials.map((s) => (
                  <li key={s.name}>
                    <a href={s.href} className="text-sm hover:text-gold-soft" target="_blank" rel="noopener noreferrer">
                      {s.name}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-12 border-t border-cream/15 pt-8">
          <p className="rounded-lg border border-gold/30 bg-sage-light/30 px-4 py-3 text-sm font-medium text-cream" data-testid="footer-not-crisis-service">
            {SITE.footerDisclaimer}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-xs text-cream/60">
            <p>© {new Date().getFullYear()} {SITE.name}. Created in loving memory of TJ.</p>
            <div className="flex items-center gap-5">
              <Link to="/privacy" className="hover:text-gold-soft hover:underline underline-offset-4" data-testid="footer-link-privacy">
                Privacy
              </Link>
              <Link to="/accessibility" className="hover:text-gold-soft hover:underline underline-offset-4" data-testid="footer-link-accessibility">
                Accessibility
              </Link>
              <button
                type="button"
                onClick={() => setLoginOpen(true)}
                aria-label="Administrator sign-in"
                className="rounded px-2 py-1 text-sm leading-none text-cream/40 transition-colors hover:text-gold-soft"
                data-testid="admin-login-link"
              >
                *
              </button>
            </div>
          </div>
        </div>
      </div>
      <AdminLoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </footer>
  );
};
